import Controller from '@ember/controller';
import { action } from '@ember/object';
import { later, next, run, schedule, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type Model from '@ember-data/model';
import $ from 'jquery';

import type {
	APIData,
	BoardPreviewMap,
	RecordData,
	RequestError,
	Service,
	Snapshooter,
} from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import {
	AccountNumberFormatType,
	BillingFeature,
	ModelName,
	SnapshotFormat,
	SnapshotObjectType,
	TemplateObjectType,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { afterImagesLoaded, copyObject, equal, redirect, reload, uniq } from 'webapp/utils/common';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import PreviewUrlManager from 'webapp/models-custom/preview-url-manager';
import type Report from 'webapp/models-custom/report';
import ReportPresentManager from 'webapp/models-custom/report-present-manager';
import type ReportSlide from 'webapp/models-custom/report-slide';
import ReportUIHelper from 'webapp/models-custom/report-ui-helper';
import type Template from 'webapp/models-custom/template';
import type BoardRecord from 'webapp/models/board';
import type BoardPreviewRecord from 'webapp/models/board-preview';

// import type HelperSnapshooterService from 'webapp/services/helper/snapshooter';
import { Severity } from 'webapp/services/logger';

// the vendor-prefixed fullscreen APIs of older browsers
interface FullscreenDocument extends Omit<Document, 'exitFullscreen'> {
	fullScreen?: boolean;
	mozFullScreen?: boolean;
	webkitIsFullscreen?: boolean;
	webkitIsFullScreen?: boolean;
	exitFullscreen?: Document['exitFullscreen'];
	webkitExitFullscreen?: () => void;
	mozCancelFullScreen?: () => void;
	msExitFullscreen?: () => void;
}
interface FullscreenElement extends Omit<HTMLElement, 'requestFullscreen'> {
	requestFullscreen?: HTMLElement['requestFullscreen'];
	mozRequestFullScreen?: () => void;
	webkitRequestFullscreen?: () => void;
	msRequestFullscreen?: () => void;
}

export default class DatawallController extends Controller {
	@service('store') declare storeService: Service.Store;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;
	@service('logger') declare loggerService: Service.Logger;
	@service('socket') declare socketService: Service.Socket;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('chart-render-queue') declare chartRenderQueueService: Service.ChartRenderQueue;

	@tracked requirePassword = false;
	@tracked currentSlideIndex = 0;
	@tracked loadingReportInitialData = false;

	@tracked loadingDataForReportSlideId = '';
	@tracked reportUIHelper: ReportUIHelper | null = null;
	requestedReportId: number | null = null;
	requestedReportSlidesIds: number[] = [];

	originalLoopDelay = 0;

	hashedPassword;

	previewUrlManager: PreviewUrlManager;

	@tracked reportPresentManager: ReportPresentManager | null = null; // set only if present is active

	constructor() {
		// eslint-disable-next-line prefer-rest-params
		super(...arguments);

		this.previewUrlManager = new PreviewUrlManager(`/datawall/${this.token}`);

		if (this.isLoadedFromSnapshooter) {
			this._exposeSnapshooterFunctions();

			void this._loadInitialAssets().then(() => {
				// letting snapshooter know the page is ready to accept data
				if (window.callSnapshooter) {
					window.isSnapshooterActive = true;
					window.callSnapshooter();
				}
			});

			return;
		} else if (this.isLiveReport) {
			/*
				_exposeSnapshooterFunctions's  win.prepareView adds 'report-from-snapshooter' class when loaded from snapshooter.
				Add a different class when it's a live report, so we can make the page more responsive
			*/

			document.body.classList.add('live-report');

			this.reportUIHelper = new ReportUIHelper(
				this.mmReportsService,
				this.eventManagerService,
			);
			this.reportUIHelper.checkViewMode();

			if (this.previewUrlManager.isPresentView) {
				this.startPresentWithNotes();
			}
		}

		this._cleanupNonExistingBoardsFromPreviewMap();

		// reading password from localstorage
		let hashedPassword;
		try {
			hashedPassword = localStorage.getItem('dbox_dw');
		} catch (_e) {
			// skip
		}
		this.hashedPassword = hashedPassword;

		if (this.navigationService.isDatawallAndPasswordProtected && !hashedPassword) {
			this.requirePassword = true;
		}

		if (this.previewUrlManager.isInIframe) {
			scheduleOnce('afterRender', this, this._bindFullscreenEvent);
		}

		const report = this.report;

		const boardLoop = this.boardLoop;

		let loopDelay = 0;
		if (boardLoop?.loopDelay) {
			loopDelay = boardLoop.loopDelay;
			// we dont want to loop when loader is shown, and when data is loaded, it will overwrite it anyways
			boardLoop.directPropUpdate({ loopDelay: 0 });
		}
		this.originalLoopDelay = loopDelay;

		const previewMap = this.previewMap;

		// Fake the requested preview objects from the boards - for the looks
		const boardPreviews: (RecordData.BoardPreview.Serialized & { id: string })[] = [];
		Object.entries(previewMap).forEach(([boardId, previewHash]) => {
			const boardRecord = this.allBoards.find((item) => item.id === boardId);
			if (boardRecord) {
				boardPreviews.push({
					...(boardRecord.serialize() as RecordData.Board.Serialized),
					boardId: Number(boardId),
					previewHash,
					id: previewHash,
				});
			}
		});
		this.storeService.pushPayload({ boardPreviews });

		// update url stuff after the (fake) previews are in the store already, so this.boardOrder shows the correct data
		if (!report) {
			// skip if report, so itemIdParam set in this.initializeReport() is not overriden

			const boardIdParam = this.previewUrlManager.itemIdParam;
			if (boardIdParam) {
				let index = this.boardOrder.indexOf(+boardIdParam);

				// in case board was deleted from loop we show first board and update url
				if (index < 0) {
					index = 0;
					this.previewUrlManager.itemIdParam = String(this.boardOrder[0]);
				}

				this.currentSlideIndex = index;
			} else if (this.boardOrder.length > 0) {
				this._updatePathFromCurrentVisibleItemId();
			}
		}

		// reload page once per 12h
		setTimeout(
			() => {
				reload();
			},
			12 * 60 * 60 * 1000,
		);

		if (!this.requirePassword) {
			// don't load initial data now if we need password
			// same logic will be executed after password verification

			if (report?.id) {
				void this.loadReportData(report.id)
					.then(() => {
						this.initializeReport();
					})
					.catch();
			}

			const firstBoardRecord = this.firstBoardRecord;

			if (firstBoardRecord) {
				const boardId =
						(firstBoardRecord as BoardPreviewRecord).boardId || firstBoardRecord.id!, // this is the same as models-custom/board:boardIdForPublicUrl
					hash = previewMap[boardId];
				void this.loadBoardData(String(boardId), hash)
					.then(() => {
						this.pusherSubscribe();
						this.createActivityLog();
						this.setOriginalLoopDelay();
					})
					.catch();
			}
		}
	}

	get modelName() {
		if (this.isReport) {
			return 'report';
		} else if (this.boardLoop) {
			return 'looped dashboard';
		} else {
			return 'dashboard';
		}
	}

	get showHeader(): boolean {
		return !!this.headerModel && !this.isLoadedFromSnapshooter;
	}

	get showEditInHeader(): boolean {
		return !!this.report?.canCurrentUserEdit || !!this.firstBoard?.canCurrentUserEdit;
	}

	get isBoardLoopTemplate() {
		return this.template?.objectType === TemplateObjectType.BOARD_LOOP;
	}

	get showBottomSlider() {
		return !!this.boardLoop || this.isBoardLoopTemplate;
	}

	get headerModel() {
		return this.template || this.report;
	}

	createActivityLog() {
		if (!this.currentUserService.isLoggedIn) {
			return;
		}

		if (!this.navigationService.isDatawallAndHasAccessToBoard) {
			return;
		}

		if (!this.currentUserService.hasValidAccessToken) {
			return;
		}

		let objectId,
			eventType = ActivityLogEvent.BOARD_VIEW;

		if (this.isReport) {
			objectId = this.report?.id ?? '';
			eventType = ActivityLogEvent.REPORT_VIEW;
		} else if (this.boardLoop) {
			objectId = this.boardLoop.id;
			eventType = ActivityLogEvent.BOARD_LOOP_VIEW;
		} else {
			// Board or board preview
			const firstBoardRecord = this.firstBoardRecord;

			if (firstBoardRecord) {
				objectId =
					(firstBoardRecord as BoardPreviewRecord).boardId ||
					(firstBoardRecord as BoardRecord).id;
			}
		}

		if (objectId) {
			this.mmActivityLogsService.createAndSaveActivityLogItem(eventType, String(objectId));
		}
	}

	get showIframeFooter(): boolean {
		return (
			this.previewUrlManager.isInIframe &&
			!this.accountService.removeBrandingOnFeatures &&
			!this.billingService.whiteLabelingEnabled
		);
	}

	@cached
	get showDesignerLink(): boolean {
		return (
			!this.isLoadedFromSnapshooter &&
			this.currentUserService.isLoggedIn &&
			!!this.accountService.id &&
			this.accountService.id === this.navigationService.spaceIdOfDatawall &&
			!this.previewUrlManager.isInIframe &&
			!this.currentUserService.model?.isViewer
		);
	}

	@action
	editModel() {
		if (this.isReport) {
			this.editReport();
		} else {
			this.editBoardTemplate();
		}
	}

	editReport(): void {
		const report = this.report;
		if (this.currentUserService.isLoggedIn && report) {
			// if user is logged in, go to designer
			redirect('/reports/editor/' + report.id);
		}
	}

	editBoardTemplate(): void {
		const board = this.firstBoard;
		if (this.currentUserService.isLoggedIn && board) {
			// if user is logged in, go to designer
			redirect('/layout-designer/' + board.boardIdForPublicUrl);
		} else {
			redirect('https://app.databox.com');
		}
	}

	@action
	onSlideChange(index: number): void {
		this.currentSlideIndex = index;
		if (this.reportPresentManager) {
			this.reportPresentManager.currentSlideIndex = index;
		}
		if (this.isLiveReport) {
			this._updatePathFromCurrentVisibleItemId();
			this.onLiveReportSlideChange();
		} else {
			this._updatePathFromCurrentVisibleItemId();
		}
	}

	// --------- URL PARAMETERS ---------

	_updatePathFromCurrentVisibleItemId(): void {
		if (this.isReport) {
			const sortedSlidesIds = this.sortedSlidesIds,
				currentVisibleSlide = this.currentVisibleSlide;

			if (sortedSlidesIds.length > 1 && !!currentVisibleSlide?.id) {
				this.previewUrlManager.itemIdParam = currentVisibleSlide.id;
			}
		} else if (this.boardOrder.length > 1) {
			this.previewUrlManager.itemIdParam =
				this.currentVisibleBoard?.boardIdForPublicUrl || '';
		}
	}

	// --------- REPORT ---------

	get isReport(): boolean {
		return !!this.report;
	}

	get isLiveReport() {
		return this.isReport && !this.isLoadedFromSnapshooter;
	}

	get reportUIHelperForLiveReport() {
		// when this.isLiveReport
		return this.reportUIHelper!;
	}

	get currentSlideColorForLiveReport() {
		// when this.isLiveReport
		return this.currentVisibleSlide?.shownColor || '';
	}

	@cached
	get report(): Report | null {
		// only one report can be existing in store
		const reportId = this.mmReportsService.reportModels[0]?.id;

		let report;
		if (reportId) {
			report = this.mmReportsService.findModel(reportId);
		}

		return report || null;
	}

	get currentVisibleSlide(): ReportSlide | null {
		return this.report?.sortedSlides[this.currentSlideIndex] || null;
	}

	get sortedSlidesIds(): number[] {
		return this.report?.sortedSlidesIds || [];
	}

	get isLoadingReportSlideData() {
		return !!this.loadingDataForReportSlideId;
	}

	onLiveReportSlideChange() {
		const slide = this.currentVisibleSlide;
		if (slide) {
			schedule('afterRender', () => {
				// slide components rendered
				this.reportUIHelper?.setSlideDescriptionAvailable(!!slide.description.content);
			});

			this.getReportSlideData(slide);
		}
	}

	// --------- PAGELSS REPORT --------- //
	@cached
	get isPagelessReport() {
		return this.report?.isPageless;
	}

	loadAllReportSlidesData(): void {
		const report = this.report;
		if (!report) return;

		this.loadingDataForReportSlideId = 'loading-all';

		report.sortedSlides.forEach((slide) => {
			this.getReportSlideData(slide);
		});

		setTimeout(() => {
			this.loadingDataForReportSlideId = '';
		});
	}

	// --------- PAGELSS REPORT END --------- //

	initializeReport() {
		const slideIdParam = this.previewUrlManager.itemIdParam,
			sortedSlidesIds = this.sortedSlidesIds;

		if (this.isPagelessReport) {
			this.loadAllReportSlidesData();
		} else {
			if (slideIdParam) {
				let index = sortedSlidesIds.indexOf(+slideIdParam);

				if (index < 0) {
					index = 0;
					this.previewUrlManager.itemIdParam = String(sortedSlidesIds[0]);
				}

				this.currentSlideIndex = index;
			} else if (sortedSlidesIds.length > 0) {
				this._updatePathFromCurrentVisibleItemId();
			}

			this.onLiveReportSlideChange();
		}

		this.requestedReportId = this.report?.numericId || null;
		this.pusherSubscribe();
		this.createActivityLog();

		if (!this.currentVisibleSlide?.isTypeBoard) {
			// element is in DOM. Otherwise wait until isLoadingReportSlideData becomes false, loader is removed
			this.reportUIHelper?.scheduleUpdateSlideSize(); // after data.reportSlides is in store
		}
	}

	// --------- Report present mode -------

	presentTrackingOrigin = 'reports_public_URL';

	startPresent() {
		// present mode, browser fullscreen
		this.initializePresentMode(true);
	}

	startPresentWithNotes() {
		// present mode, extra popup window
		this.initializePresentMode();
	}

	@action
	initializePresentMode(isFullscreen = false) {
		const report = this.report;
		if (!report) {
			return;
		}

		this.analyticsService.event(TrackingEvent.PRESENT_SLIDE, {
			type: isFullscreen ? 'present' : 'present_notes',
			origin: this.presentTrackingOrigin,
		});

		this.onSlideChange(0);

		this.previewUrlManager.isPresentView = true;
		this.mmReportsService.triggerOuterSlideResize();

		this.reportPresentManager = new ReportPresentManager(
			report,
			this.onSlideChange,
			this.stopPresentMode,
			isFullscreen,
			this.navigationService.theme.id,
		);
	}

	@action
	private stopPresentMode() {
		this.previewUrlManager.isPresentView = false;
		this.mmReportsService.triggerOuterSlideResize();
		this.reportPresentManager = null;
	}

	// --------- SHOWN BOARDS ---------

	get token(): string {
		return this.requestService.wallToken || '';
	}

	@cached
	get boardLoop(): BoardLoop | null {
		return (
			this.mmBoardLoopsService.boardLoopModels.find((item) => item.token === this.token) ||
			null
		);
	}

	get allBoards(): BoardRecord[] {
		return (this.storeService.peekAll(ModelName.BOARD) as BoardRecord[]).filter(
			(item) => item.id && !item.isDeleted,
		);
	}

	get allBoardPreviews(): BoardPreviewRecord[] {
		return (this.storeService.peekAll(ModelName.BOARD_PREVIEW) as BoardPreviewRecord[]).filter(
			(item) => item.id && !item.isDeleted,
		);
	}

	get firstBoardRecord(): BoardRecord | BoardPreviewRecord | null {
		if (this.report) {
			return null;
		}

		let board: BoardRecord | undefined;

		const boardLoop = this.boardLoop;
		if (boardLoop) {
			board = this.allBoards.find((item) => item.id === String(boardLoop.boardIds[0]));
		} else {
			board = this.allBoards.find((item) => item.token === this.token);
		}

		let firstBoardRecord;
		if (board) {
			firstBoardRecord = this._getBoardRecordFromId(board.id!); // will check for previews
		} else {
			// this should happen in case we have a revoked board token
			if (!this.isLoadedFromSnapshooter && this.allBoards.length > 0 && this.token) {
				reload(); // should fall on error screen
			}
		}
		return firstBoardRecord || null;
	}

	_firstBoard: Board | null = null;

	get firstBoard(): Board | null {
		const boardRecord = this.firstBoardRecord,
			cached = this._firstBoard;

		if (boardRecord) {
			if (cached?.id !== boardRecord.id) {
				// eslint-disable-next-line ember/no-side-effects
				this._firstBoard = this.mmBoardsService.createModelFromRecord(boardRecord);
			}
		} else {
			// eslint-disable-next-line ember/no-side-effects
			this._firstBoard = null;
		}

		return this._firstBoard;
	}

	_boardOrder: number[] = [];

	get boardOrder(): number[] {
		let boardOrder: number[] = [];

		const boardLoop = this.boardLoop,
			board = this.firstBoardRecord;
		if (boardLoop) {
			boardOrder = uniq(boardLoop.boardIds);
		} else if (board) {
			boardOrder = [
				(board as BoardPreviewRecord).previewHash
					? (board as BoardPreviewRecord).boardId
					: +board.id!,
			];
		}

		if (!equal(boardOrder, this._boardOrder)) {
			// eslint-disable-next-line ember/no-side-effects
			this._boardOrder = boardOrder;
		}

		return this._boardOrder;
	}

	_boards: Board[] = [];

	get boards(): Board[] {
		// create Board objects from boardOrder ids (updating cache)
		// we reuse Boards so we don't have to reset board-state

		const boards: Board[] = [],
			okIds = this.boardOrder;

		this._boards.forEach((boardCM: Board) => {
			const boardId = boardCM.boardIdForPublicUrl,
				previewHash = this.previewMap[boardId],
				recordType = `board${previewHash ? '-preview' : ''}`;
			if (okIds.includes(+boardId)) {
				if ((boardCM.model.constructor as typeof Model).modelName === recordType) {
					boards.push(boardCM);
				}
			} else {
				// cleanup removed
				this.mmBoardsService.markBoardNotRequested(boardId);
				this.garbageCollectorService._unloadBoardData(boardId, previewHash);
				if (previewHash) {
					this.socketService.unsubscribeFromBoardPreview(previewHash);
				}
				next(() => {
					this.storeService.unloadRecord(boardCM.model);
				});
			}
		});

		// add the new ones
		const cachedIds = boards.map((item) => item.boardIdForPublicUrl);
		okIds.forEach((numericId: number) => {
			const boardId = String(numericId);
			if (!cachedIds.includes(boardId)) {
				const board = this._getBoardRecordFromId(boardId);
				if (board) {
					const boardCM = this.mmBoardsService.createModelFromRecord(board);
					boards.push(boardCM);
					if (boardCM.allBlocks.length) {
						next(() => {
							this.mmBoardsService.markBoardRequested(String(boardId));
						});
					}
				}
			}
		});

		// sorting!
		boards.sort((a, b) => {
			const idx1 = okIds.indexOf(+a.boardIdForPublicUrl),
				idx2 = okIds.indexOf(+b.boardIdForPublicUrl);
			return idx1 < idx2 ? -1 : 1;
		});

		if (
			!equal(
				this._boards.map((b) => +b?.boardIdForPublicUrl),
				okIds,
			)
		) {
			// totaly different boards / slides (possibly just changed order)
			// eslint-disable-next-line ember/no-side-effects
			this._boards = [...boards];
		} else {
			if (
				!equal(
					this._boards.map((b) => b?.id),
					boards.map((item) => item.id),
				)
			) {
				// same boardIdForPublicUrl-s, but diff IDs which means board and preview changed between eachother
				// do not reset whole array, just switch items
				for (let i = 0; i < this._boards.length; i++) {
					const matchingBoard = boards[i];
					if (matchingBoard && this._boards[i]?.id !== matchingBoard.id) {
						// eslint-disable-next-line ember/no-side-effects
						this._boards[i] = matchingBoard;
					}
				}
			}
		}

		return this._boards;
	}

	get currentVisibleBoard() {
		return this.boards[this.currentSlideIndex];
	}

	private setOriginalLoopDelay() {
		if (this.boardLoop && this.originalLoopDelay) {
			// set it back to previous value after first board is loaded
			this.boardLoop.directPropUpdate({ loopDelay: this.originalLoopDelay });
		}
		this.originalLoopDelay = 0;
	}

	// -------- PREVIEWS -------------

	get previewMap() {
		return this.previewUrlManager.previewMap;
	}

	set previewMap(value: BoardPreviewMap) {
		this.previewUrlManager.previewMap = value;
	}

	_cleanupNonExistingBoardsFromPreviewMap(): void {
		const boardOrder = this.boardOrder;

		if (boardOrder.length > 0) {
			const previewMap = this.previewMap;
			Object.keys(previewMap).forEach((key) => {
				if (!boardOrder.includes(+key)) {
					delete previewMap[key];
				}
			});

			this.previewMap = previewMap;
		}
	}

	resetPreviewToOriginal(boardId: string): void {
		// re-request the data for this specific boardId
		const boardObj = this.boards.find((item) => item.boardIdForPublicUrl === boardId);

		if (boardObj?.isPreview) {
			this.mmBoardsService.markBoardNotRequested(boardId);
			void this.loadBoardData(boardId).catch();
		}
	}

	@action
	boardStatusHashChanged(boardId: string, previewHash?: string | null): void {
		const previewMap = copyObject(this.previewMap);
		if (previewHash) {
			previewMap[boardId] = previewHash;
		} else {
			delete previewMap[boardId];
			this.resetPreviewToOriginal(boardId);
		}
		this.previewMap = previewMap;
	}

	// --------- LOADING REPORT DATA ------------
	loadReportData(reportId: string): Promise<void> {
		return new Promise((resolve, reject) => {
			this.loadingReportInitialData = true;

			this._doReportDataRequest(reportId)
				.then(
					(reply) => {
						const success = this._handleResponse(reply);
						if (success) {
							resolve();
						} else {
							reject('password');
						}
					},
					(errorData: RequestError) => {
						this.requestService.handleGenericThrownError(errorData);
						reject('request');
					},
				)
				.catch(() => {
					reject('other');
				})
				.finally(() => {
					this.loadingReportInitialData = false;
				});
		});
	}

	_doReportDataRequest(
		reportId: string,
		password?: string,
	): Promise<APIData.Datawall.DataResponse> {
		const sendData: APIData.Datawall.DataRequest = {},
			savedPw = password || this.hashedPassword;

		const url = `/token/${this.token}/report/${reportId}`;

		if (savedPw) {
			sendData.password = encodeURIComponent(savedPw);
		}

		return this.requestService.api<APIData.Datawall.DataResponse>('GET', url, sendData);
	}

	getReportSlideData(reportSlide: ReportSlide): void {
		const reportSlideId = reportSlide.numericId!;

		if (this.requestedReportSlidesIds.includes(reportSlideId)) {
			return;
		}

		if (
			!reportSlide.fragments.some(
				(fragment) =>
					fragment.isTypeBlock || fragment.isTypeStoryText || fragment.isTypeBoard,
			)
		) {
			return;
		}

		const sendData: APIData.Datawall.DataRequest = {},
			savedPw = this.hashedPassword;

		this.loadingDataForReportSlideId = reportSlide.id ?? '';

		if (savedPw) {
			sendData.password = encodeURIComponent(savedPw);
		}

		this.mmReportSlidesService
			.getReportSlideDataWithToken(this.token, reportSlide.id!, sendData)
			.then((reply) => {
				this._markBoardsAndPreviewsAsRequested(reply);
				this.requestedReportSlidesIds.push(reportSlideId);
			})
			.catch(() => {
				// skip error
			})
			.finally(() => {
				setTimeout(() => {
					// using next shows "missing databoard" message for a while
					if (this.loadingDataForReportSlideId === (this.currentVisibleSlide?.id ?? '')) {
						this.loadingDataForReportSlideId = ''; // Board can be read from store
					}

					// element is in DOM - isLoadingReportSlideData ( <Reports::Viewer @isLoaderShown ) is false, board slide rendered instead of bar loader
					this.reportUIHelper?.scheduleUpdateSlideSize();
					scheduleOnce(
						'afterRender',
						this.mmReportsService,
						this.mmReportsService.triggerInnerSlideResize,
					);
				});
			});
	}

	// --------- LOADING BOARD DATA ------------

	_loadBoardDataPromise: Promise<void> | null = null;

	loadBoardData(boardId: string, previewHash?: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const requestPromise = this._doBoardDataRequest(boardId, previewHash)
				.then(
					(reply) => {
						const success = this._handleResponse(reply, true);
						if (success) {
							this._loadBoardDataPromise = null;
							this.loadNextBoardData();
							resolve();
						} else {
							reject('password');
						}
					},
					(errorData: RequestError) => {
						this.requestService.handleGenericThrownError(errorData);
						reject('request');
					},
				)
				.catch(() => {
					reject('other');
				})
				.finally(() => {
					this.mmBoardsService.boardsRequestingData =
						this.mmBoardsService.boardsRequestingData.filter((r) => r.id !== boardId);
				});

			this.mmBoardsService.boardsRequestingData = [
				...this.mmBoardsService.boardsRequestingData,
				{
					id: boardId,
					promise: requestPromise,
				},
			];
		});
	}

	loadNextBoardData(): void {
		if (this._loadBoardDataPromise) {
			return;
		}

		let boardIdToLoad = '';
		const currentVisibleBoardId = this.currentVisibleBoard?.boardIdForPublicUrl;
		if (currentVisibleBoardId && !this._isBoardDataLoaded(currentVisibleBoardId)) {
			boardIdToLoad = currentVisibleBoardId;
		} else {
			this.boardOrder.some((boardId) => {
				const boardIdString = String(boardId);
				if (!this._isBoardDataLoaded(boardIdString)) {
					boardIdToLoad = boardIdString;
					return true;
				}
				return false;
			});
		}

		if (boardIdToLoad) {
			const hash = this.previewMap[boardIdToLoad];
			this._loadBoardDataPromise = this.loadBoardData(boardIdToLoad, hash).catch();
		}
	}

	_isBoardDataLoaded(boardId: string): boolean {
		return (
			this.mmBoardsService.isBoardDataRequested(boardId) ||
			this.mmBoardsService.isBoardDataRequesting(boardId)
		);
	}

	_doBoardDataRequest(
		boardId: string,
		previewHash?: string,
		password?: string,
	): Promise<APIData.Datawall.DataResponse> {
		const sendData: APIData.Datawall.DataRequest = {},
			savedPw = password || this.hashedPassword;

		let url = `/datawalls/${this.token}/boards/${boardId}`;

		if (savedPw) {
			sendData.password = encodeURIComponent(savedPw);
		}

		if (previewHash) {
			sendData.previewHash = previewHash;
		}

		return this.requestService
			.api<APIData.Datawall.DataResponse>('GET', url, sendData)
			.then((reply) => {
				// this request triggers samples that come over pusher dataUpdated / bgEvent
				void this.mmBoardPreviewsService
					.getPromiseAfterPreviewDataChannelSubscribed(sendData.previewHash)
					.then(() => {
						url += '/data';
						this.requestService
							.api<RecordData.Payload>('GET', url, sendData)
							.then((samplesReply) => {
								this.storeService.pushPayload(samplesReply);
							})
							.catch(
								this.requestService.handleGenericThrownError.bind(
									this.requestService,
								),
							);
					})
					.catch();

				return reply;
			});
	}

	_markBoardsAndPreviewsAsRequested(data: APIData.Datawall.DataResponse, isSingle = false) {
		// mark as requested
		if (isSingle) {
			this.mmBoardsService.markBoardRequested(String(data.board!.id)); // a single board request carries its board
		} else {
			(data.boards || []).forEach((board) => {
				this.mmBoardsService.markBoardRequested(String(board.id));
			});
		}

		if (data.boardPreview) {
			this.mmBoardPreviewsService.markPreviewRequested(data.boardPreview.previewHash);
		} else {
			(data.boardPreviews || []).forEach((preview) => {
				this.mmBoardPreviewsService.markPreviewRequested(preview.previewHash);
			});
		}
	}

	_handleResponse(
		data: APIData.Datawall.DataResponse,
		isSingle = false,
		includeBgImages = true,
	): boolean {
		if (data.password === 'required' || data.password === 'failed') {
			this.requirePassword = true;

			return false;
		} else {
			this.requirePassword = false;

			this._markBoardsAndPreviewsAsRequested(data, isSingle);

			// set preview map based on data returned to be used later on filtering boards
			// some hashes might be incorrect or not up to date
			// BE will skip them, and so should we
			if (isSingle) {
				const boardId = data.board!.id;
				if (this.previewMap[boardId] && data.boardPreview?.boardId !== +boardId) {
					// unload the invalid preview record so we can switch previews later without issues
					const previewToRemove = this.previewMap[boardId],
						recordToRemove = this.storeService.peekRecord(
							ModelName.BOARD_PREVIEW,
							previewToRemove,
						) as BoardPreviewRecord;
					if (recordToRemove) {
						recordToRemove.unloadRecord();
					}
					delete this.previewMap[boardId];

					this.previewMap = this.previewMap;
				}
			} else {
				let mustSet = false;
				Object.keys(this.previewMap).forEach((boardId) => {
					if (!(data.boardPreviews || []).find((item) => item.boardId === +boardId)) {
						delete this.previewMap[boardId];
						mustSet = true;
					}
				});
				if (mustSet) {
					this.previewMap = this.previewMap;
				}
			}

			// update the following props only from primary board request (spaceId set) or snapshooter
			if (data.spaceId || this.isLoadedFromSnapshooter) {
				const spaceId = Number(data.spaceId);
				this.navigationService.spaceIdOfDatawall = spaceId;
				this.loggerService.spaceId = spaceId;
				delete data.spaceId;

				this.navigationService.isDatawallAndHasAccessToBoard = !!data.hasAccess; // also works for clients, used in conjunction with allowAnonymousDateRangeEdit/allowAnonymousFilterEdit
				delete data.hasAccess;

				this.accountService.appLogo = data.appLogo || '';
				delete data.appLogo;

				this.accountService.removeBrandingOnFeatures = !!data.removeBrandingOnFeatures;
				delete data.removeBrandingOnFeatures;

				this.accountService.metadata = data.metadata!; // kept as is when missing (snapshooter data)
				delete data.metadata;

				if (typeof data.firstDayOfWeek !== 'undefined') {
					this.accountService.firstDayOfWeek = data.firstDayOfWeek;
					delete data.firstDayOfWeek;
				}

				// reset to default if not returned for some reason
				this.accountService.numberFormatType =
					typeof data.numberFormatType === 'undefined'
						? AccountNumberFormatType.AMERICAN
						: data.numberFormatType;
				this.accountService.updateActiveNumeralLocale();

				this.accountService.appLogoEnabled = !!data.appLogoEnabled;
				delete data.appLogoEnabled;

				this.accountService.companyLogo = data.companyLogo || '';
				delete data.companyLogo;

				this.accountService.companyLogoEnabled = !!data.companyLogoEnabled;
				delete data.companyLogoEnabled;

				this.billingService.updateFeatureInfo({
					[BillingFeature.WHITE_LABELING]: this.billingService.whiteLabelingEnabled, // don't overwrite what's been loaded in app-loaders/datawall
					[BillingFeature.SKIP_DW_BRANDING]: !!data.hideDatawallBranding,
					[BillingFeature.INSTANT_REFRESH]: !!data.instantRefreshEnabled,
					[BillingFeature.VISUALIZATIONS_ADVANCED]: true, // always true to not show any limitations. BE will not return these blocks if they're disabled
					[BillingFeature.GENERATIVE_AI]: !!data.generativeAIEnabled,
					[BillingFeature.ADVANCED_REPORTS]: !!data.reportsAdvancedSlidesEnabled,
				});
				delete data.hideDatawallBranding;
				delete data.instantRefreshEnabled;
				delete data.generativeAIEnabled;
				delete data.reportsAdvancedSlidesEnabled;
			}

			if (!includeBgImages) {
				(data.boards || [data.board]).forEach((board) => {
					if (board?.settings?.bg) {
						delete board.settings.bg;
					}
				});
				(data.boardPreviews || [data.boardPreview]).forEach((board) => {
					if (board?.settings?.bg) {
						delete board.settings.bg;
					}
				});
				(data.reportSlides || [data.reportSlide]).forEach((slide) => {
					if (slide?.settings?.bg) {
						delete slide.settings.bg;
					}
				});
			}

			// push new data in
			this.storeService.pushPayload(data);

			return true;
		}
	}

	// ------------ SNAPSHOOTER SPECIFICS -----------
	get isLoadedFromSnapshooter(): boolean {
		return this.navigationService.isLoadedFromSnapshooter;
	}

	get hideSliderBeforeRerender(): boolean {
		return this.isLoadedFromSnapshooter && !this.boards.length;
	}

	async loadDataForSnapshooter(
		data: APIData.Datawall.DataResponse,
		token: string,
		boardMap: BoardPreviewMap,
		includeBgImages: boolean,
	): Promise<void> {
		const boardIds: string[] = [],
			previewMap: BoardPreviewMap = {};

		// boardMap keys are all boards that snapshooter needs
		// not a given each key will have a preview though
		Object.keys(boardMap).forEach((boardId: string) => {
			boardIds.push(boardId);
			if (boardMap[boardId]) {
				previewMap[boardId] = boardMap[boardId];
			}
		});

		this._cleanup();

		// check if everything has been cleaned!
		await new Promise((resolve, reject) => {
			schedule('afterRender', () => {
				if ($('#slider-ct').length) {
					reject({
						type: 'slider',
					});
				}
				resolve(null);
			});
		});

		this.requestService.wallToken = token;
		this.previewUrlManager.rootUrl = `/datawall/${token}`;
		this.previewMap = previewMap;

		this._handleResponse(data, false, includeBgImages);

		await new Promise((resolve) => {
			schedule('afterRender', () => {
				later(resolve, true, 300); // just in case
			});
		});
	}

	_exposeSnapshooterFunctions(): void {
		const win = window as Snapshooter.WindowWithInjections;

		win.loadData = async (
			data: APIData.Datawall.DataResponse,
			token = '',
			boardMap: BoardPreviewMap = {},
			includeBgImages = false,
		) => {
			await this.loadDataForSnapshooter(data, token, boardMap, includeBgImages);

			let orderedIds: number[] = [],
				objectType = -1,
				objectId = -1,
				objectTitle = '';
			if (this.isReport) {
				const report = this.report!;
				// if pageless snapshooter, use report id instead
				orderedIds = report.isPageless
					? [report.numericId!]
					: (report.sortedSlides || []).map((slide) => slide.numericId!);
				objectType = SnapshotObjectType.REPORT;
				if (report) {
					objectId = report.numericId!;
					objectTitle = report.title;
				}
			} else {
				const boardLoop = this.boardLoop;
				if (boardLoop) {
					objectType = SnapshotObjectType.LOOP;
					objectId = boardLoop.numericId!;
					objectTitle = boardLoop.title;
				} else {
					// single board
					const firstBoard = this.firstBoard;
					if (firstBoard) {
						objectId = firstBoard.numericId!;
						objectTitle = firstBoard.name;
						objectType = SnapshotObjectType.BOARD;
					}
				}
				const requestedBoards = Object.keys(boardMap).map((boardId) => +boardId);
				orderedIds = this.boardOrder.filter((boardId) => {
					return requestedBoards.includes(boardId);
				});
			}

			document.title = objectTitle;

			return {
				objectType,
				objectId,
				objectTitle,
				orderedIds,
			};
		};

		win.prepareView = async (itemId: number, format: number) => {
			const response: { contentHeight?: number } = {};

			if (this.isReport) {
				const report = this.report!;
				const slides = report.sortedSlides || [];
				// for images, we need to hide nav and description
				// for pageless adjust styling for thumbnail
				const baseClass = 'report-from-snapshooter';
				const isImage = format === SnapshotFormat.JPG;
				const reportBodyCt = $('#report-viewer_body');

				document.body.classList.add(baseClass);

				if (report.isPageless) {
					reportBodyCt.addClass('pageless-mode');
					if (isImage) {
						// add additional styling for pageless preview thumbnail image
						reportBodyCt.addClass('thumbnail-view');
					}

					window.dispatchEvent(new Event('resize'));

					// preload all images from all slides
					const allImages = slides.flatMap((slide) => slide.allImages || []);
					await this._preloadImages(allImages);
					await this._waitForRenderCycles(true);
				} else {
					const slide = slides.find((item) => item.id === String(itemId));

					if (!slide) {
						throw {
							type: 'slide not found',
							slideId: itemId,
							reportId: this.report?.id,
						};
					}

					const slideIndex = slides.indexOf(slide);
					this.currentSlideIndex = slideIndex;

					document.body.classList.toggle(`${baseClass}--hide-desc`, isImage);
					window.dispatchEvent(new Event('resize'));

					await this._preloadImages(slide.allImages);
					await this._waitForRenderCycles(true);

					// make sure the slide title and the title we are seeing MATCH
					// to avoid leaking the wrong data
					// take only the first for the cases where we have multiple boards on same slide (pageless issue, no reproduce)
					const slideTitleCt = $('.report_slide .bottomInfo h1').eq(0);
					if (slideTitleCt.length) {
						const shownTitle = slideTitleCt.text().trim(),
							boardOrPreview = slide.boardOrPreview,
							expectedTitle = (
								(boardOrPreview ? boardOrPreview.name : slide.title) || ''
							).trim();
						if (shownTitle !== expectedTitle) {
							throw {
								type: 'title mismatch',
								requestedSlideId: itemId,
								shownTitle,
								expectedTitle,
							};
						}
					}
				}

				// get the total height of pageless report
				response.contentHeight = Math.floor(
					report.isPageless
						? (reportBodyCt.prop('scrollHeight') as number)
						: reportBodyCt.height()!,
				);
			} else {
				const model = this.boards.find(
					(item) => item.boardIdForPublicUrl === String(itemId),
				);

				if (!model) {
					throw {
						type: 'board not found',
						boardId: itemId,
					};
				}

				const boardIndex = this.boards.indexOf(model);

				$('#slider-ct').slick('slickGoTo', boardIndex);
				this.currentSlideIndex = boardIndex;

				const previewHash = this.previewMap[itemId];

				await this._preloadImages(model.allImages);

				await this._waitForRenderCycles();

				// make sure the board title and the title we are seeing MATCH
				// to avoid leaking the wrong board
				// templates do not show title, so no check
				if (!this.template) {
					const shownTitle = $('.slide.slick-current .bottomInfo h1').text().trim(),
						expectedTitle = model.name.trim();
					if (shownTitle !== expectedTitle) {
						throw {
							type: 'title mismatch',
							requestedBoardId: itemId,
							shownTitle,
							expectedTitle,
							previewHash,
						};
					}
				}
			}

			return response;
		};

		// snapshot done, cleanup
		win.cleanupData = () => {
			this._cleanup();
		};
	}

	_cleanup(): void {
		this.infolayerService.closeAll();
		this.storeService.unloadAll();
		this.currentSlideIndex = 0;
	}

	_loadInitialAssets(): Promise<[void, void]> {
		const usedImages = [
			'https://cdn1.databox.com/images/logo/databox-allwhite.svg',
			'https://cdn1.databox.com/images/logo/databox-dark.svg',
			'https://cdn1.databox.com/images/logo/databox-signonly-margin.svg',
		];

		const imagesLoadPromise = this._preloadImages(usedImages);

		const reportFontsLoadPromise = this.mmReportsService.loadFonts();

		return Promise.all([reportFontsLoadPromise, imagesLoadPromise]);
	}

	async _preloadImages(images: string[]): Promise<void> {
		//let images = model.get('allImages');
		const settledPromises = await afterImagesLoaded(images);
		settledPromises
			.filter((item): item is PromiseRejectedResult => item.status === 'rejected')
			.forEach((settledPromise) => {
				// logging images that could not load
				this.loggerService.logError(
					'Snapshooter: Failed to load assets',
					{
						reason: 'image',
						// afterImagesLoaded rejects with the image load status
						type: (settledPromise.reason as { path?: string } | undefined)?.path,
					},
					Severity.INFO,
				);
			});
	}

	async _waitForRenderCycles(isReport = false): Promise<void> {
		// wait for a little, then hook into chart render service callback if needed
		await new Promise<void>((resolve) => {
			next(() => {
				schedule('afterRender', () => {
					later(() => {
						if (this.chartRenderQueueService.checkIfHasWork()) {
							this.eventManagerService.on(
								AppEvent.CHART_RENDER_QUEUE_DONE,
								this,
								() => {
									resolve();
									return true;
								},
							);
						} else {
							resolve();
						}
					}, 100);
				});
			});
		});
		await new Promise((resolve) => setTimeout(resolve, 200)); // just in case
		if (isReport) {
			// aaaand some extra
			await new Promise((resolve) => setTimeout(resolve, 500));
		}
	}

	// ----------- FULLSCREEN EVENT ----------

	_bindFullscreenEvent(): void {
		$(document).on(
			'webkitfullscreenchange.datawall mozfullscreenchange.datawall fullscreenchange.datawall',
			() => {
				run(() => {
					const doc = document as FullscreenDocument,
						isFullScreen =
							document.fullscreen ||
							doc.fullScreen ||
							doc.mozFullScreen ||
							doc.webkitIsFullscreen;
					$('body').toggleClass('iframe-fullscreen', isFullScreen);
				});
			},
		);
	}

	@action
	toggleFullscreen(): void {
		const doc = document as FullscreenDocument,
			isFullScreen =
				document.fullscreen ||
				doc.fullScreen ||
				doc.mozFullScreen ||
				doc.webkitIsFullScreen;

		if (isFullScreen) {
			try {
				// exiting - use document
				const el = document as FullscreenDocument;
				if (el.exitFullscreen) {
					void el.exitFullscreen();
				} else if (el.webkitExitFullscreen) {
					el.webkitExitFullscreen();
				} else if (el.mozCancelFullScreen) {
					el.mozCancelFullScreen();
				} else if (el.msExitFullscreen) {
					el.msExitFullscreen();
				}
			} catch (_e) {
				// skip
			}
		} else {
			try {
				// entering - use root element
				const el = document.documentElement as FullscreenElement;
				if (el.requestFullscreen) {
					void el.requestFullscreen();
				} else if (el.mozRequestFullScreen) {
					el.mozRequestFullScreen();
				} else if (el.webkitRequestFullscreen) {
					el.webkitRequestFullscreen();
				} else if (el.msRequestFullscreen) {
					el.msRequestFullscreen();
				} else {
					window.open(window.location.href, '_blank');
				}
			} catch (_e) {
				// skip
			}
		}
		$('#slider').trigger('resize.datawallcenter');
	}

	// ------------ TEMPLATE ? -----------

	_template: Template | null = null;

	get template(): Template | null {
		const firstTemplateRecord = this.mmTemplatesService.savedRecords[0];

		if (firstTemplateRecord) {
			if (this._template?.id !== firstTemplateRecord.id) {
				// eslint-disable-next-line ember/no-side-effects
				this._template = this.mmTemplatesService.createModel(firstTemplateRecord);
			}
		} else {
			// eslint-disable-next-line ember/no-side-effects
			this._template = null;
		}

		document.body.classList.toggle('is-public-template', !!this._template?.isPublic);

		return this._template;
	}

	get isTemplate(): boolean {
		return !!this.template;
	}

	// --------------- HELPER FNS --------------

	_getBoardRecordFromId(id: string): BoardRecord | BoardPreviewRecord | null {
		const previewHash = this.previewMap[id];

		let board: BoardRecord | BoardPreviewRecord | undefined;
		if (previewHash) {
			board = this.allBoardPreviews.find((b) => {
				return b.boardId === +id && b.previewHash === previewHash;
			});
		} else {
			board = this.allBoards.find((b) => {
				return b.id === id;
			});
		}

		return board || null;
	}

	setSavedPassword(hashedPassword: string): void {
		// Safari in Private Browsing Mode supports localStorage but all calls to setItem throw QuotaExceededError
		try {
			localStorage.setItem('dbox_dw', hashedPassword);
		} catch (_e) {
			// skip
		}
	}

	@action
	verifyPassword(password: string): Promise<APIData.Datawall.DataResponse> {
		const reportId = this.report?.id,
			firstBoardRecord = this.firstBoardRecord,
			checkPassword = (data: APIData.Datawall.DataResponse) => {
				if (data.password === 'success') {
					this.setSavedPassword(data.cookie!); // sent with password 'success'
					this.hashedPassword = data.cookie;
					this.requirePassword = false;
					return Promise.resolve();
				} else {
					return Promise.reject();
				}
			};

		if (reportId) {
			return this._doReportDataRequest(reportId, password).then((data) => {
				void checkPassword(data).then(() => {
					void this.loadReportData(reportId).then(() => {
						this.initializeReport();
					});
				});

				return data;
			});
		} else if (firstBoardRecord) {
			const id = (firstBoardRecord as BoardPreviewRecord).previewHash
				? String((firstBoardRecord as BoardPreviewRecord).boardId)
				: firstBoardRecord.id!; // alternative of boardIdForPublicUrl on board custom-model
			return this._doBoardDataRequest(id, this.previewMap[id], password).then((data) => {
				void checkPassword(data).then(() => {
					void this.loadBoardData(id, this.previewMap[id]).then(() => {
						this.pusherSubscribe();
						this.createActivityLog();
						this.setOriginalLoopDelay();
					});
				});
				return data;
			});
		} else {
			return Promise.reject();
		}
	}

	@action
	checkIsPublicEnabled() {
		if (this.isLoadedFromSnapshooter) {
			return;
		}
		const itemWithToken = this.report || this.boardLoop || this.firstBoard;
		if (itemWithToken && !itemWithToken.isPublicEnabled) {
			reload(); // will show error page
		}
	}

	pusherSubscribe(): void {
		// initialize pusher (with or without password)
		this.socketService.subscribeToAppEvents(true, {
			// onReconnect: function(){
			// 	this.getOriginalWallData();
			// },
			password: this.hashedPassword,
			isIframe: this.previewUrlManager.isInIframe,
		});
	}
}
